import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Send, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE = "9099369442";
const EMAIL = "info@omeraexports.com";

export default function Contact() {
  const { t, lang } = useI18n();
  const c = t.contact;

  const waText =
    lang === "ar"
      ? "مرحباً Omera Exports، أرغب في الاستفسار عن مجموعتكم من الأجهزة."
      : "Hi Omera Exports, I'd like to enquire about your hardware collection.";
  const WHATSAPP_LINK = `https://wa.me/91${PHONE}?text=${encodeURIComponent(waText)}`;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(c.error_required);
      return;
    }
    try {
      setLoading(true);
      await axios.post('https://formspree.io/f/xaqgkllp', form);
      setIsSubmitted(true);
      setForm({ name: "", email: "", phone: "", company: "", interest: "", message: "" });
      
      // Trigger brochure download
      const link = document.createElement('a');
      link.href = '/Omera_Exports_Catalogue_2026.pdf';
      link.download = 'Omera_Exports_Catalogue_2026.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      toast.error(c.error_send);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 lg:py-36 bg-[#FFFFFF] border-t border-[#0A0A0A]/10"
      data-testid="contact-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="overline mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-[#9CB4A9]" /> {c.overline}
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A] font-light">
            {c.title_a}
            <br />
            <span className="italic">{c.title_b}</span>
          </h2>
          <p className="mt-6 text-[#0A0A0A] max-w-md leading-relaxed">{c.body}</p>

          <div className="mt-10 space-y-1 border-t border-[#0A0A0A]/10">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-link"
              className="group flex items-center justify-between py-5 border-b border-[#0A0A0A]/10 hover:border-[#9CB4A9]/40 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 border border-[#0A0A0A]/15 group-hover:border-[#25D366] flex items-center justify-center transition">
                  <MessageCircle size={18} className="text-[#25D366]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#0A0A0A]">
                    {c.whatsapp}
                  </div>
                  <div className="text-[#0A0A0A] text-base">+91 {PHONE}</div>
                </div>
              </div>
              <ArrowUpRight size={18} className="text-[#0A0A0A] group-hover:text-[#9CB4A9] transition" />
            </a>

            <a
              href={`tel:+91${PHONE}`}
              data-testid="contact-phone-link"
              className="group flex items-center justify-between py-5 border-b border-[#0A0A0A]/10 hover:border-[#9CB4A9]/40 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 border border-[#0A0A0A]/15 group-hover:border-[#9CB4A9] flex items-center justify-center transition">
                  <Phone size={18} className="text-[#9CB4A9]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#0A0A0A]">
                    {c.phone}
                  </div>
                  <div className="text-[#0A0A0A] text-base">+91 {PHONE}</div>
                </div>
              </div>
              <ArrowUpRight size={18} className="text-[#0A0A0A] group-hover:text-[#9CB4A9] transition" />
            </a>

            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-link"
              className="group flex items-center justify-between py-5 border-b border-[#0A0A0A]/10 hover:border-[#9CB4A9]/40 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 border border-[#0A0A0A]/15 group-hover:border-[#9CB4A9] flex items-center justify-center transition">
                  <Mail size={18} className="text-[#9CB4A9]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#0A0A0A]">
                    {c.email}
                  </div>
                  <div className="text-[#0A0A0A] text-base break-all">{EMAIL}</div>
                </div>
              </div>
              <ArrowUpRight size={18} className="text-[#0A0A0A] group-hover:text-[#9CB4A9] transition" />
            </a>

            <div className="flex items-center gap-4 py-5">
              <div className="w-11 h-11 border border-[#0A0A0A]/15 flex items-center justify-center">
                <MapPin size={18} className="text-[#9CB4A9]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#0A0A0A]">
                  {c.atelier}
                </div>
                <div className="text-[#0A0A0A] text-base">{c.location}</div>
              </div>
            </div>
          </div>


        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7 lg:ps-8"
          data-testid="inquiry-form"
        >
          <div className="border border-[#0A0A0A]/10 p-8 lg:p-12 bg-[#FAFAFA]">
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-[#0A0A0A]/10">
              <div>
                <div className="overline mb-2">{c.form_overline}</div>
                <div className="font-display text-2xl text-[#0A0A0A]">
                  {c.form_title}
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#0A0A0A]">
                {c.form_id}
              </span>
            </div>

            {isSubmitted ? (
              <div className="py-20 text-center text-[#0A0A0A] flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-[#9CB4A9]/20 rounded-full flex items-center justify-center mb-6">
                  <Send className="text-[#9CB4A9]" size={28} />
                </div>
                <h3 className="text-3xl font-display mb-3">Form submitted</h3>
                <p className="text-[#0A0A0A]/70 max-w-sm">Experts will get in touch soon.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field
                label={c.f_name}
                required
                value={form.name}
                onChange={(v) => update("name", v)}
                testid="form-name"
              />
              <Field
                label={c.f_email}
                type="email"
                required
                value={form.email}
                onChange={(v) => update("email", v)}
                testid="form-email"
              />
              <Field
                label={c.f_phone}
                value={form.phone}
                onChange={(v) => update("phone", v)}
                testid="form-phone"
              />
              <Field
                label={c.f_company}
                value={form.company}
                onChange={(v) => update("company", v)}
                testid="form-company"
              />
            </div>

            <div className="mt-6">
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#0A0A0A] block mb-3">
                {c.f_interest}
              </label>
              <div className="flex flex-wrap gap-2">
                {c.interests.map((opt, i) => {
                  const active = form.interest === opt;
                  return (
                    <button
                      key={i}
                      type="button"
                      data-testid={`interest-${i}`}
                      onClick={() => update("interest", active ? "" : opt)}
                      className={`px-4 py-2 text-xs tracking-wide border transition-colors ${
                        active
                          ? "bg-[#9CB4A9] text-black border-[#9CB4A9]"
                          : "border-[#0A0A0A]/15 text-[#0A0A0A] hover:border-[#9CB4A9]/60 hover:text-[#0A0A0A]"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#0A0A0A] block mb-3">
                {c.f_message} <span className="text-[#9CB4A9]">*</span>
              </label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder={c.f_placeholder}
                data-testid="form-message"
                className="w-full bg-transparent border border-[#0A0A0A]/15 focus:border-[#9CB4A9] focus:ring-1 focus:ring-[#9CB4A9] outline-none p-4 text-[#0A0A0A] placeholder:text-[#0A0A0A] transition-colors resize-none"
              />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-[#0A0A0A] max-w-sm">{c.consent}</p>
              <button
                type="submit"
                disabled={loading}
                data-testid="form-submit"
                className="group inline-flex items-center justify-center gap-3 bg-[#9CB4A9] hover:bg-[#8CA499] disabled:opacity-50 text-black px-8 py-4 text-sm font-medium tracking-wide transition-colors"
              >
                {loading ? c.submitting : c.submit}
                <Send size={16} className="transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
            </>
            )}
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, required, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-[#0A0A0A] block mb-3">
        {label} {required && <span className="text-[#9CB4A9]">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border-b border-[#0A0A0A]/15 focus:border-[#9CB4A9] outline-none py-3 text-[#0A0A0A] placeholder:text-[#0A0A0A] transition-colors"
      />
    </div>
  );
}
